import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../context/AuthContext";
import { Ship, GraduationCap, Loader2, ExternalLink } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const AdminPartners = () => {
  const { toast } = useToast();
  const [partners, setPartners] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/partners").then((r) => { setPartners(r.data.partners); setOrgs(r.data.organisations); })
      .catch(() => toast({ title: "Could not load badges", variant: "destructive" }))
      .finally(() => setLoading(false));
  }, [toast]);

  const link = async (p, uid) => {
    try {
      await api.put(`/admin/partners/${p.id}`, { linked_user_id: uid || null });
      setPartners((l) => l.map((x) => (x.id === p.id ? { ...x, linked_user_id: uid || null } : x)));
      toast({ title: uid ? "Badge linked" : "Badge unlinked" });
    } catch (e) { toast({ title: "Could not save", description: formatApiError(e?.response?.data?.detail), variant: "destructive" }); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="admin-partners">
      <div className="border-b border-slate-100 p-4">
        <h2 className="font-heading text-base font-bold text-slate-900">Home page partner badges</h2>
        <p className="text-xs text-slate-500">Link a badge to a registered company or institute and it becomes clickable on the home page, opening their public Indosbook page.</p>
      </div>
      {loading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div> : (
        <ul className="divide-y divide-slate-100">
          {orgs.length === 0 && <li className="px-4 py-3 text-xs text-slate-400">No company or institute accounts have registered yet — badges stay non-clickable until one joins.</li>}
          {partners.map((p) => {
            const Icon = p.type === "fleet" ? Ship : GraduationCap;
            const options = orgs.filter((o) => (p.type === "fleet" ? o.role === "company" : o.role === "institute"));
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`admin-partner-${p.id}`}>
                <Icon className="h-4 w-4 shrink-0 text-[#FF4500]" />
                <span className="min-w-[220px] flex-1 text-sm font-semibold text-slate-800">{p.name}</span>
                <Select value={p.linked_user_id || "none"} onValueChange={(v) => link(p, v === "none" ? null : v)}>
                  <SelectTrigger className="h-9 w-[280px] rounded-full" data-testid={`admin-partner-select-${p.id}`}>
                    <SelectValue placeholder="Not linked" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not linked</SelectItem>
                    {options.map((o) => <SelectItem key={o.user_id} value={o.user_id} data-testid={`admin-partner-option-${p.id}-${o.user_id}`}>{o.name} ({o.email})</SelectItem>)}
                  </SelectContent>
                </Select>
                {p.linked_user_id && (
                  <Link to={`/org/${p.linked_user_id}`} target="_blank" className="inline-flex items-center gap-1 text-xs font-bold text-[#002855] hover:underline">
                    View page <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AdminPartners;
